/** @jsx jsx */
// External libraries
import { jsx } from '@emotion/core';
import { useStoreState } from '@/store';

// Internal modules
import { features, getContent, hasSelectAllSignupTerms } from '@/lib';
import { getTermsLinks } from './Components';
import { useMountLogging } from '@/lib/reactHooks';
import { getOnboardTermsKey } from './utils';
import { StyledOnboardTermsText } from './StyledComponents';
import { OnboardTermsWithCheckbox } from './OnboardTermsWithCheckbox';
import type { AppMode, InputOnChange } from '@/types';
import { APP_MODE } from '@/types';

interface OnboardTermsProps {
  changeHandler: InputOnChange;
  type: AppMode;
}

export const OnboardTerms = ({
  changeHandler,
  type,
}: OnboardTermsProps): JSX.Element => {
  const isGuest = type === APP_MODE.GUEST;
  const featureName = isGuest
    ? 'hasGuestTermsAndConditionsCheckbox'
    : 'hasSignupTermsAndConditionsCheckbox';

  useMountLogging(`${type}OnboardTerms`);
  const hasSelectAllExp = hasSelectAllSignupTerms();
  const isSubmitting = useStoreState(({ ui }) => ui.isFormSubmitting);
  const complianceKey = getOnboardTermsKey(type);
  const termsLinks = getTermsLinks(complianceKey, isSubmitting);
  const termsString = getContent(complianceKey, termsLinks);
  const hasTermsCheckbox = features.isActive(featureName);
  const hasSelectAllAndSignup = hasSelectAllExp && type === APP_MODE.SIGNUP;

  /* Renders the terms without the checkbox (StyledOnboardTermsText) if:
   * it doesnt have the hasTermsCheckbox feature
   * it doenst have both: hasSelectAllSignupTerms & its signup flow
   * Select All is a functionality for KR and shouldn't affect guest flow
   */

  if (!hasTermsCheckbox && !hasSelectAllAndSignup) {
    return (
      <StyledOnboardTermsText data-testid="onboard-terms">
        {termsString}
      </StyledOnboardTermsText>
    );
  }

  return (
    <OnboardTermsWithCheckbox
      changeHandler={changeHandler}
      label={termsString}
      errorContentKey={
        hasSelectAllExp ? 'fieldErrors.common.valueMissing' : undefined
      }
      type={type}
    />
  );
};
